import React from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';

import { Button, Callout, CopyButton, Icon, Skeleton } from '@/nova/components';
import { Sheet, useSheetDismiss } from '@/nova/components/Sheet';
import { useAppSelector } from '@/store';
import { selectCreativeJobsMap, type CreativeJob } from '@/store/slices/creativeJobs';

import { adSubjectFromGame, adSubjectFromItem, subjectLandingUrl } from '../../ad-subjects';
import { withCreated } from '../../ad-versions';
import { adTabRoutes, type AdView } from '../../asset-routes';
import { invalidateLandingDestinations } from '../../hooks/useLandingDestinations';
import type { AdSubject } from '../../banner-kit-api';
import type { CreativeItem } from '../../creative-api';
import {
  fetchLandingLegal,
  fetchLandingTemplates,
  fetchMarketingOffers,
  isImmersive,
  isPublicationOf,
  LANDING_FEATURED_GAMES_MAX,
  liveFragmentDocument,
  resolveLandingTemplate,
  landingHeroUrl,
  landingPosterVersion,
  newLandingParams,
  previewLandingPage,
  primaryHost,
  shortMoid,
  unpublishLandingPage,
  upsertLandingPage,
  type LandingFeatured,
  type LandingOffer,
  type LandingPage,
  type LandingTemplate,
  type LivePage,
  type MarketingOffer,
} from '../../landing-page-api';
import { useAssetLibraryStats } from '../../useAssetLibraryStats';
import { LINKS_SHEET_PARAM, STATS_SHEET_PARAM, useSheetParam } from '../../useSheetParam';
import {
  useLandingPageViews,
  viewsKey,
  viewsNote as viewsNoteOf,
} from '../../../website-analytics/useLandingPageViews';
import { useWebsiteTrafficQualityGate } from '../../../website-analytics/quality/gate';
import { useGameRecords } from '../../useGameRecords';
import { detectWelcomeBonus } from '../../welcome-bonus';
import { AdLibraryShell, type AdCardState, type AdLibraryShellItem } from '../AdLibraryShell';
import { AdSubjectFlow } from '../AdSubjectFlow';
import { AssetCardStats, AssetCardStatsSkeleton, AssetStatsSheet } from '../AssetStats';
import { LandingPreview } from '../LandingPageComposer/LandingPreview';
import { LandingPageComposer } from '../LandingPageComposer';
import { LandingTemplatePicker, type TemplatesRead } from '../LandingPageComposer/LandingTemplatePicker';
import { LandingLinks } from '../LandingPageComposer/LandingLinks';
import {
  allLinksPerLandingPage,
  assetLinkPath,
  linkForFromSearch,
  linksPerLandingPage,
  saveLinkFor,
} from '../LandingPageComposer/link-for';
import { sendLinkHere } from '../LandingPageComposer/link-for-send';
import { MadeForBar } from '../LandingPageComposer/MadeForBar';
import { LivePagePoster } from './LivePagePoster';
import { useLandingPageLibrary } from './useLandingPageLibrary';
import { useLivePages } from './useLivePages';
import * as S from './LandingPageLibrary.styles';

/** A page live on the brand's landing domain that was not made in the Studio: listed with the Studio's own pages
 *  (same card), opened view-only. */
interface LiveEntry extends AdLibraryShellItem {
  live: LivePage;
}

/** One card of the library: a landing page made in the Studio, or a live page made elsewhere. */
type LibraryEntry = LandingPage | LiveEntry;

/** The library's stats window (its sign-ups look back 90 days, see useAssetLibraryStats): the Views use the same. */
const LIBRARY_WINDOW_MS = 90 * 24 * 60 * 60 * 1000;

const isLiveEntry = (e: LibraryEntry): e is LiveEntry => 'live' in e;

/** A Studio page with a template (on its draft or a version): one that was set up. Pure. */
const hasTemplate = (p: LandingPage): boolean =>
  Boolean(p.ad_params.template_id) || p.ad_versions.some((v) => Boolean(v.params.template_id));

/** What a new page leads with: the promotion or games the owner picked (featured). Its subject is always the welcome
 *  offer, read from the pool when the page is saved. */
interface NewPageChoice {
  featured?: LandingFeatured;
}

/** A new page's brand facts: the website's default language, all its languages, and its default offer. */
interface NewPageBrand {
  language: string;
  languages: string[];
  offer: LandingOffer;
}

export interface LandingPageLibraryProps {
  tenantId?: number;
  brandName: string;
  /** The brand's site domain (a new page's default call-to-action link). */
  brandDomain?: string;
  /** The brand's promotion pool (a new page is about its welcome bonus; the composer's promotion and hero pickers). */
  items: CreativeItem[];
  itemsLoaded: boolean;
  /** The deep-linked view (the library, a new page, or a saved page by id), derived from the URL by the page. */
  view: AdView;
  onBuyCredits: (needed?: number) => void;
  onNotify: (message: string) => void;
}

export type { LiveRead } from './useLivePages';

/** Placeholder cards while the first read is in flight. */
const SKELETON_CARDS = 3;

/** The language a new page starts in when the brand's are unknown. */
const FALLBACK_LANGUAGE = 'en';

/** How long a page made for an asset waits for that asset's subject to be found (the promotion pool, the game's record
 *  in the catalogue) before the owner picks it instead. A network bound, not an animation. */
const AUTO_SUBJECT_WAIT_MS = 15000;

const isActive = (job: CreativeJob | undefined): boolean => job?.status === 'queued' || job?.status === 'running';

/** A saved page's card state: a generation in flight, generated (a locked version), or a draft. Pure. */
const cardState = (p: LandingPage, jobsById: Record<string, CreativeJob>): AdCardState => {
  if (isActive(p.job_id ? jobsById[p.job_id] : undefined)) return 'generating';
  return p.ad_versions.some((v) => v.locked) ? 'ready' : 'draft';
};

/** A short fingerprint of a text (FNV-1a, base 36): tells two versions of a page's content apart. Pure. */
const fingerprint = (text: string): string => {
  let h = 0x811c9dc5;
  for (let i = 0; i < text.length; i += 1) {
    h ^= text.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return (h >>> 0).toString(36);
};

/** A page address without its scheme, for a card line ("lp.brand.com/welcome"). Pure. */
const bareUrl = (url: string): string => url.replace(/^https?:\/\//i, '').replace(/\/+$/, '');

/**
 * Marketing Assets > Landing Pages: the brand's saved landing pages on the shared ad library shell (create tile, a
 * card each with the page drawn at a phone width as its poster (a full-screen game's whole phone screen) and where it
 * is live, Edit / Archive, archived sheet),
 * then the pages live on its landing domain that were not made here (a card each: the page drawn from its own HTML,
 * never the live URL, its title, path and address with copy and open controls, and the offer it promotes). "New"
 * navigates to `/landing-pages/new`, which asks what the page promotes, then its template, and only then saves a draft
 * converting to the welcome bonus (with every language of the brand's website and its offer) and swaps the URL to its
 * id in place; a card opens `/landing-pages/:id`. The URL is the source of truth, so back / forward and reloads
 * work.
 */
export const LandingPageLibrary = ({
  tenantId,
  brandName,
  brandDomain,
  items,
  itemsLoaded,
  view,
  onBuyCredits,
  onNotify,
}: LandingPageLibraryProps): React.ReactElement => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const routes = adTabRoutes('landing-pages');
  const {
    items: pages,
    loading,
    loaded,
    settled,
    listFailure,
    refresh,
    remove,
    restore,
    purge,
    patch,
  } = useLandingPageLibrary(tenantId);
  const jobsById = useAppSelector(selectCreativeJobsMap);
  const viewId = view.kind === 'edit' ? view.id : undefined;
  const { search } = useLocation();

  /* ── The brand's tracking links (each card counts the ads' links landing on it) and their figures ── */

  // One read for the grid: the brand's links (shared and cached; read again each time a composer closes, since it may
  // have moved one) and the grouped activity of every link landing on each listed page (its own included). A card's
  // figures open the page's stats sheet.
  const onGrid = view.kind === 'library';
  const statIds = React.useMemo(() => pages.filter((p) => !p.archived && hasTemplate(p)).map((p) => p.id), [pages]);
  const stats = useAssetLibraryStats('landing_page', statIds, { onGrid, idsLoaded: loaded && listFailure === null });
  // Each published page's Views over the same window as its sign-ups (the last 90 days), from the analytics service.
  const viewPaths = React.useMemo(
    () =>
      pages.filter((p) => !p.archived && hasTemplate(p) && p.publication?.path).map((p) => p.publication?.path ?? ''),
    [pages],
  );
  const [viewsSpan] = React.useState(() => ({ from: Date.now() - LIBRARY_WINDOW_MS, to: Date.now() }));
  const traffic = useWebsiteTrafficQualityGate(tenantId);
  const views = useLandingPageViews(tenantId, viewPaths, viewsSpan, {
    enabled: traffic.websiteAnalytics,
    quality: traffic.quality,
  });
  const viewsOf = (p: LandingPage): number | null => {
    const path = p.publication?.path;
    if (!path || views.status !== 'ready') return null;
    // A page the held counts do not cover (published since, and the re-read failed) is unknown, not 0.
    return views.byPath.get(viewsKey(path)) ?? null;
  };
  // The stats sheet open over the grid is the address's (`?stats=<id>`), so Back from one of its deep links reopens it.
  // Read on the grid only: a composer open over it reads the same key for its own sheet.
  const statsSheet = useSheetParam(STATS_SHEET_PARAM);
  const statsForId = view.kind === 'library' ? statsSheet.value : null;
  const heldLinks = stats.links;
  const adLinkCounts = React.useMemo(() => linksPerLandingPage(heldLinks ?? []), [heldLinks]);
  const allLinkCounts = React.useMemo(() => allLinksPerLandingPage(heldLinks ?? []), [heldLinks]);
  // The page whose "N ad links" chip was clicked (`?links=<id>`, on the grid only, as the stats sheet): its links sheet
  // lists the same held links the chip counted.
  const linksSheet = useSheetParam(LINKS_SHEET_PARAM);
  const linksForId = view.kind === 'library' ? linksSheet.value : null;

  /* ── The live pages (and the brand's landing hosts) ── */

  const { current, live: liveData, reload: reloadLive } = useLivePages(tenantId);

  // The offers name each page's `moid`; a failed read leaves the raw id on the card.
  const [offers, setOffers] = React.useState<{ tenantId: number; list: MarketingOffer[] } | null>(null);
  React.useEffect(() => {
    if (!tenantId) return undefined;
    const controller = new AbortController();
    void fetchMarketingOffers(tenantId, controller.signal).then((res) => {
      if (controller.signal.aborted) return;
      setOffers({ tenantId, list: res.ok ? res.data.offers : [] });
    });
    return () => controller.abort();
  }, [tenantId]);

  // The template catalogue (read once per page load and shared): a full-screen game's card shows a whole phone screen,
  // and a new page's template step offers it.
  const [templatesRead, setTemplatesRead] = React.useState<TemplatesRead>({ status: 'loading' });
  const creating = view.kind === 'new';
  React.useEffect(() => {
    // Reads spark-creatives' catalogue on mount, and again as a new page starts: the library stays mounted across its
    // views, and a read that failed or came back empty is not kept (fetchLandingTemplates asks again), so it is not
    // held until the page reloads.
    let alive = true;
    setTemplatesRead((held) => (held.status === 'ready' && held.list.length > 0 ? held : { status: 'loading' }));
    void fetchLandingTemplates().then((list) => {
      if (alive) setTemplatesRead(list ? { status: 'ready', list } : { status: 'error' });
    });
    return () => {
      alive = false;
    };
  }, [creating]);
  const templates = React.useMemo(() => (templatesRead.status === 'ready' ? templatesRead.list : []), [templatesRead]);

  /* ── A new page: saved as a draft first, then edited by id ── */

  const [created, setCreated] = React.useState<LandingPage | null>(null);
  const createdRef = React.useRef(false);
  const createGenRef = React.useRef(0);
  const welcome = React.useMemo(() => detectWelcomeBonus(items), [items]);
  // What the new page promotes, picked on the subject step; the template step follows, and nothing is saved before a
  // template is picked (no card in the grid for a page that has none).
  const [choice, setChoice] = React.useState<NewPageChoice | null>(null);
  const [minting, setMinting] = React.useState(false);
  // A page made FOR an asset's tracking link ("New landing page…" in its link panel): the context its URL carries. It
  // is kept for the page once saved (the save swaps the URL to the page's id without the query, see link-for.ts).
  const linkForQuery = React.useMemo(
    () => (view.kind === 'new' ? linkForFromSearch(search) : null),
    [view.kind, search],
  );
  // The asset's subject, when given, is picked for the owner (once; the template step's Back returns to the picker).
  const autoType =
    linkForQuery?.subjectType === 'promotion' || linkForQuery?.subjectType === 'game' ? linkForQuery.subjectType : null;
  const autoId = autoType ? linkForQuery?.subjectId ?? '' : '';
  const [autoDone, setAutoDone] = React.useState(false);

  // The library does not remount across /landing-pages <-> /new <-> /:id (the page keys it by tab), so returning to
  // the grid resets the new-page session; the grid is the one view with no mint in flight.
  React.useEffect(() => {
    if (view.kind !== 'library') return;
    createdRef.current = false;
    createGenRef.current += 1; // invalidate any in-flight mint or brand read
    setCreated(null);
    setChoice(null);
    setAutoDone(false);
    // An invalidated mint returns before it clears its own flag: without this every later "Create new" showed the
    // setting-up sheet forever.
    setMinting(false);
  }, [view.kind]);

  // The page always claims the welcome offer: its subject is the welcome promotion (the button's link and the offer
  // copy); what the owner picked first is what it FEATURES (the copy, the scene and the lettering lead with it), unless
  // that pick is the welcome promotion itself.
  const welcomeSubject = React.useMemo<AdSubject>(
    () =>
      welcome
        ? adSubjectFromItem(welcome)
        : { id: '', name: '', image_url: '', headline: '', subtitle: '', cta_text: '' },
    [welcome],
  );
  const mint = React.useCallback(
    (picked: NewPageChoice, brandFacts: NewPageBrand, templateId: string): void => {
      if (!tenantId || createdRef.current) return;
      createdRef.current = true;
      setMinting(true);
      const gen = (createGenRef.current += 1);
      // The welcome promotion as the pool has it NOW (not when the subject was picked: the pool may not have answered
      // then, and a page saved without its welcome offer would never get it back).
      const { featured } = picked;
      const subject = welcomeSubject;
      const name =
        (featured?.type === 'game' ? featured.items.map((g) => g.name).join(' · ') : featured?.items[0]?.name) ||
        subject.name ||
        t('Untitled landing page');
      const fail = (status: number): void => {
        if (gen !== createGenRef.current) return;
        createdRef.current = false;
        setMinting(false);
        onNotify(
          status === 404
            ? t("Saving landing pages isn't available in this environment yet.")
            : t("That couldn't be created. Please try again."),
        );
        navigate(routes.library(), { replace: true });
      };
      // A full-screen game's own copy (prizes, call to play, win modal) is seeded from its template.
      const adParams = newLandingParams({
        subject,
        ctaUrl: subjectLandingUrl(subject, brandDomain),
        language: brandFacts.language,
        languages: brandFacts.languages,
        templateId,
        offer: brandFacts.offer,
        template: templates.find((tpl) => tpl.id === templateId),
      });
      if (featured) adParams.featured = featured;
      void upsertLandingPage(tenantId, {
        name,
        status: 'draft',
        image_url: subject.image_url,
        ad_params: adParams,
      }).then((res) => {
        // A page made for an asset: that asset version's link is pointed at it now (the durable record every tab reads
        // back; it goes live with the page's first publish), and the context is kept under the page's id for the
        // composer (its return path, and a retry should the pointing fail). Also when the owner already left.
        if (res.ok && linkForQuery) {
          saveLinkFor(tenantId, res.data, linkForQuery);
          void sendLinkHere(tenantId, res.data, linkForQuery);
        }
        if (gen !== createGenRef.current) {
          // Left before the save answered: the page exists anyway (it has its template), so the grid re-reads to show
          // it rather than leave the owner to make it again.
          if (res.ok) refresh();
          return;
        }
        if (!res.ok) {
          fail(res.status);
          return;
        }
        const id = res.data;
        setMinting(false);
        setCreated({
          id,
          tenant_id: String(tenantId),
          name,
          media_type: 'landing_page',
          image_url: subject.image_url,
          status: 'draft',
          archived: false,
          ad_params: adParams,
          ad_versions: [],
        });
        window.history.replaceState(window.history.state, '', routes.edit(id));
        refresh();
      });
    },
    [tenantId, brandDomain, welcomeSubject, templates, routes, refresh, onNotify, navigate, t, linkForQuery],
  );

  // `new` first asks what the page promotes (the same subject step as the other marketing assets: promotions or up to
  // 4 games), then its template; the draft is saved only once both are picked, and the composer opens on it.
  // The brand website's languages (every one, the default first) and its default offer, read as the subject is picked
  // so the template step's cards are drawn in the page's language (each read is quick; a failed one leaves its
  // default).
  const [brand, setBrand] = React.useState<{ gen: number; facts: NewPageBrand } | null>(null);
  const choose = (picked: NewPageChoice): void => {
    if (!tenantId) return;
    setChoice(picked);
    const gen = createGenRef.current;
    void Promise.all([fetchLandingLegal(tenantId), fetchMarketingOffers(tenantId)]).then(([legal, offerRead]) => {
      if (gen !== createGenRef.current) return;
      const offerList = offerRead.ok && offerRead.data.available ? offerRead.data.offers : [];
      const pick = offerList.find((o) => o.is_default && o.is_active) ?? offerList.find((o) => o.is_active);
      setBrand({
        gen,
        facts: {
          language: (legal.ok && legal.data.default_language) || FALLBACK_LANGUAGE,
          languages: legal.ok ? legal.data.languages : [],
          offer: pick ? { moid: pick.moid, name: pick.name } : { moid: '', name: '' },
        },
      });
    });
  };
  const brandFacts = brand && brand.gen === createGenRef.current ? brand.facts : null;

  // Picking the made-for asset's subject waits for where it is found: the promotion pool, or the game's full record (read
  // by id, only for a game: its key art is the page's picture). One that is not found (or not in time) leaves the pick
  // to the owner.
  const autoPending = view.kind === 'new' && autoType !== null && autoId !== '' && !autoDone && !choice && !created;
  const autoGame = autoPending && autoType === 'game';
  const { byId: autoGames, loading: gamesLoading } = useGameRecords(autoGame ? [autoId] : [], autoGame);
  const chooseRef = React.useRef(choose);
  chooseRef.current = choose;
  React.useEffect(() => {
    // Syncs with the promotion pool and the game's record (read elsewhere): the subject once it can be looked up.
    if (!autoPending) return;
    if (autoType === 'promotion') {
      if (!itemsLoaded) return;
      const item = items.find((i) => i.banner_id === autoId);
      setAutoDone(true);
      if (!item) return;
      const picked = adSubjectFromItem(item);
      if (picked.id && picked.id === welcomeSubject.id) chooseRef.current({});
      else chooseRef.current({ featured: { type: 'promotion', items: [picked] } });
      return;
    }
    if (gamesLoading) return;
    const game = autoGames.get(autoId);
    setAutoDone(true);
    if (game) chooseRef.current({ featured: { type: 'game', items: [adSubjectFromGame(game, '1:1')] } });
  }, [autoPending, autoType, autoId, itemsLoaded, items, welcomeSubject.id, gamesLoading, autoGames]);
  React.useEffect(() => {
    // A timer: a subject that cannot be looked up in time is left to the owner's pick.
    if (!autoPending) return undefined;
    const timer = window.setTimeout(() => setAutoDone(true), AUTO_SUBJECT_WAIT_MS);
    return () => window.clearTimeout(timer);
  }, [autoPending]);
  const chosePromotion = (_type: unknown, picked: AdSubject): void => {
    if (picked.id && picked.id === welcomeSubject.id) choose({});
    else choose({ featured: { type: 'promotion', items: [picked] } });
  };
  const choseGames = (games: AdSubject[]): void =>
    choose({ featured: { type: 'game', items: games.slice(0, LANDING_FEATURED_GAMES_MAX) } });
  // The page the template cards are drawn from (its subject, language and offer; no template yet).
  const choiceParams = React.useMemo(
    () =>
      choice && brandFacts
        ? newLandingParams({
            subject: welcomeSubject,
            ctaUrl: subjectLandingUrl(welcomeSubject, brandDomain),
            language: brandFacts.language,
            languages: brandFacts.languages,
            templateId: '',
            offer: brandFacts.offer,
          })
        : null,
    [choice, brandFacts, brandDomain, welcomeSubject],
  );

  // `edit`: resolved from the list. Only a SETTLED, successful list for this brand may declare the id missing; then
  // back to the grid with a notice. A failed read is not an answer (the grid shows its error and a reload retries).
  React.useEffect(() => {
    if (view.kind !== 'edit' || !viewId || !loaded || loading) return;
    if (listFailure !== null && listFailure !== 404) return;
    // The asset minted here moments ago is not missing while the list catches up: opening its stats or links sheet moves
    // the address from /new to its id before the post-mint refresh has landed it.
    if (created && created.tenant_id === String(tenantId) && created.id === viewId) return;
    if (!pages.some((p) => p.id === viewId)) {
      onNotify(
        listFailure === 404
          ? t("Saving landing pages isn't available in this environment yet.")
          : t("That landing page couldn't be found."),
      );
      navigate(routes.library(), { replace: true });
    }
  }, [view.kind, viewId, loaded, loading, pages, listFailure, created, tenantId, routes, onNotify, navigate, t]);

  const goToLibrary = React.useCallback((): void => navigate(routes.library()), [navigate, routes]);
  const startNew = React.useCallback((): void => navigate(routes.create()), [navigate, routes]);
  const openPage = React.useCallback((p: LandingPage): void => navigate(routes.edit(p.id)), [navigate, routes]);
  // The composer's optimistic edits land on the list entry, and on the freshly saved snapshot too while the list has
  // not brought it in yet.
  const patchPage = React.useCallback(
    (id: string, fields: Partial<LandingPage>): void => {
      patch(id, fields);
      setCreated((cur) => (cur && cur.id === id ? { ...cur, ...fields } : cur));
    },
    [patch],
  );

  /* ── Archiving a live page: it is unpublished first (archiving alone would leave it served) ── */

  const [archiveLive, setArchiveLive] = React.useState<{ page: LandingPage; resolve: (ok: boolean) => void } | null>(
    null,
  );
  const [takingDown, setTakingDown] = React.useState(false);

  // A live page made outside the Studio, open view-only (preview, address, offer; nothing to edit).
  const [viewing, setViewing] = React.useState<LivePage | null>(null);
  const { closeHandle: viewingCloseHandle } = useSheetDismiss(() => setViewing(null));
  const archiveLiveRef = React.useRef(archiveLive);
  archiveLiveRef.current = archiveLive;
  const cancelArchiveLive = React.useCallback((): void => {
    archiveLiveRef.current?.resolve(false);
    setArchiveLive(null);
  }, []);
  const { closeHandle: archiveLiveCloseHandle, dismiss: dismissArchiveLive } = useSheetDismiss(cancelArchiveLive);

  /** Take a live page offline (the landing app's page is deleted). Several seconds. */
  const takeDown = React.useCallback(
    async (p: LandingPage): Promise<boolean> => {
      if (!tenantId) return false;
      const res = await unpublishLandingPage(tenantId, p.id);
      if (!res.ok) {
        onNotify(t("The page couldn't be unpublished, so it was not archived. Please try again."));
        return false;
      }
      patch(p.id, { publication: res.data.publication });
      reloadLive();
      invalidateLandingDestinations(tenantId);
      return true;
    },
    [tenantId, patch, onNotify, t, reloadLive],
  );

  const removePage = React.useCallback(
    (id: string): Promise<boolean> => {
      const p = pages.find((x) => x.id === id);
      if (p?.publication?.state !== 'live') return remove(id);
      return new Promise<boolean>((resolve) => setArchiveLive({ page: p, resolve }));
    },
    [pages, remove],
  );

  const confirmArchiveLive = async (): Promise<void> => {
    const pending = archiveLive;
    if (!pending || takingDown) return;
    setTakingDown(true);
    try {
      const down = await takeDown(pending.page);
      const ok = down ? await remove(pending.page.id) : false;
      if (down)
        onNotify(ok ? t('The page is offline and archived.') : t('The page is offline, but it could not be archived.'));
      pending.resolve(ok);
    } finally {
      setTakingDown(false);
      setArchiveLive(null);
    }
  };

  // A page archived while it was live (before this rule) is taken offline before it is deleted for good.
  const purgePage = React.useCallback(
    async (id: string): Promise<boolean> => {
      const p = pages.find((x) => x.id === id);
      if (p?.publication?.state === 'live' && !(await takeDown(p))) return false;
      return purge(id);
    },
    [pages, purge, takeDown],
  );

  /* ── Derived ── */

  const host = liveData ? primaryHost(liveData.hosts) : undefined;
  const offerList = offers && offers.tenantId === tenantId ? offers.list : [];
  const offerLabel = (moid: string): string => {
    if (!moid) return t('No offer');
    const offer = offerList.find((o) => o.moid === moid);
    return offer?.name || shortMoid(moid);
  };
  // The live pages made here are shown on their own card, not again under "Live on your domain".
  const ownLive = (page: LivePage): boolean => pages.some((p) => isPublicationOf(page, p.publication));
  const otherLive = liveData ? liveData.pages.filter((page) => !ownLive(page)) : [];
  // A page with no template was never set up (the template step came after the save before): not listed.
  const listed = pages.filter((p) => hasTemplate(p));
  const entries: LibraryEntry[] = [
    ...listed,
    ...otherLive.map(
      (page): LiveEntry => ({
        id: `live:${page.lpid || page.path}`,
        name: page.title || page.name || page.path,
        archived: false,
        live: page,
      }),
    ),
  ];

  const editing = view.kind !== 'library';
  // The freshly minted draft counts only for the brand it was made for: right after a brand switch it must not stand
  // in for the (still empty) new brand's list, or its editor would render, and save, under the new brand.
  const createdHere = created && created.tenant_id === String(tenantId) ? created : null;
  const reopen: LandingPage | undefined =
    view.kind === 'new'
      ? createdHere
        ? withCreated(pages, createdHere)
        : undefined
      : viewId
        ? createdHere?.id === viewId
          ? withCreated(pages, createdHere)
          : pages.find((p) => p.id === viewId)
        : undefined;
  // The card of the page open in the composer draws the page as it was when the composer opened: the composer's edits
  // land on the list as they are typed, and each would otherwise redraw (and read again) the poster hidden under it.
  // Closing the composer draws the edited page once.
  const [openedAs, setOpenedAs] = React.useState<LandingPage | null>(null);
  if (editing && reopen && openedAs?.id !== reopen.id) setOpenedAs(reopen);
  else if (!editing && openedAs) setOpenedAs(null);

  const composerNode =
    editing && reopen && tenantId ? (
      <LandingPageComposer
        // A brand switch remounts the composer: one mounted across it would save this page under the new brand.
        key={`${tenantId}:${reopen.id}`}
        tenantId={tenantId}
        page={reopen}
        brandName={brandName}
        brandDomain={brandDomain}
        items={items}
        live={liveData}
        liveFailed={current?.status === 'error'}
        patch={patchPage}
        onLiveChanged={reloadLive}
        onClose={goToLibrary}
        onBuyCredits={onBuyCredits}
        onNotify={onNotify}
        onNavigate={(path) => navigate(path)}
      />
    ) : null;

  /* ── Cards ── */

  // How many of the ads' tracking links land on the page (its own link not counted); none: no chip.
  const linkChip = (p: LandingPage): React.ReactNode => {
    const n = adLinkCounts.get(p.id) ?? 0;
    if (n === 0) return null;
    const label = n === 1 ? t('1 ad link') : t('{{n}} ad links', { n });
    return (
      <S.LinkChip
        type="button"
        title={t('Tracking links of your ads that send visitors to this page')}
        onClick={() => linksSheet.open(p.id)}
      >
        <Icon name="megaphone" size={12} />
        <S.LinkChipText raw text={label} />
      </S.LinkChip>
    );
  };

  const publicationLine = (p: LandingPage): React.ReactNode => {
    const pub = p.publication;
    const chip = linkChip(p);
    if (!pub && !chip) return null;
    return (
      <S.PubRow>
        {pub ? (
          <>
            <S.PubDot
              $tone={pub.state === 'live' ? 'live' : pub.state === 'failed' ? 'failed' : 'off'}
              aria-hidden="true"
            />
            {pub.state === 'live' ? (
              <S.PubText text="Live at {{url}}" placeholders={{ url: bareUrl(pub.url) || pub.path }} />
            ) : pub.state === 'failed' ? (
              <S.PubText text="Publishing failed" />
            ) : (
              <S.PubText text="Unpublished" />
            )}
          </>
        ) : null}
        {chip}
      </S.PubRow>
    );
  };

  // A live page's card footer: the same "Live at" line as a published Studio page.
  const liveLine = (page: LivePage): React.ReactNode => (
    <S.PubRow>
      <S.PubDot $tone={page.url ? 'live' : 'off'} aria-hidden="true" />
      {page.url ? (
        <S.PubText text="Live at {{url}}" placeholders={{ url: bareUrl(page.url) || page.path }} />
      ) : (
        <S.PubText text="No public address yet" />
      )}
    </S.PubRow>
  );

  // The live list's own states, under the cards: it failed, or the brand has no landing domain yet. Its pages
  // themselves are cards in the grid above.
  const liveNotice = (): React.ReactNode => {
    if (!tenantId || !current) return null;
    if (current.status === 'error') {
      return (
        <S.ErrorRow>
          <Callout
            tone="danger"
            text={
              current.httpStatus === 404
                ? t("Landing pages aren't available in this environment yet.")
                : t("The pages live on your landing domain couldn't be loaded.")
            }
          />
          <Button variant="outline" icon="refresh" label={t('Try again')} onClick={reloadLive} />
        </S.ErrorRow>
      );
    }
    if (current.data.hosts.length > 0) return null;
    return (
      <S.NoDomain>
        <Icon name="globe" size={20} />
        <S.NoDomainCopy>
          <S.NoDomainTitle text="This brand has no landing domain yet" />
          <S.NoDomainText text="Once your landing domain is set up, the pages live on it show here and yours can be published to it. You can already compose and preview a new page." />
        </S.NoDomainCopy>
      </S.NoDomain>
    );
  };

  const ownPoster = (entry: LandingPage): React.ReactNode => {
    const p = openedAs?.id === entry.id ? openedAs : entry;
    const version = landingPosterVersion(p);
    // The page's template, by its id or a retired id it replaced.
    const template = resolveLandingTemplate(templates, version?.params.template_id ?? '');
    const immersive = isImmersive(template);
    // A template with a scene has no hero: its mobile background stands in until the page has loaded.
    const hero =
      (template?.scene ? version?.params.scene.background_mobile_url : landingHeroUrl(version)) || p.image_url;
    if (!version || !version.params.template_id || !tenantId) {
      return hero ? (
        <LivePagePoster imageUrl={hero} title={p.name} layout="mobile" />
      ) : (
        <S.PosterPlaceholder>
          <Icon name="globe" size={22} />
          <S.PosterEmpty text="No template chosen yet" />
        </S.PosterPlaceholder>
      );
    }
    // The document drawn is the version's content: a draft edited in place draws (and is held) anew.
    const cacheKey = `${p.id}:${version.created_at}:${fingerprint(JSON.stringify([version.params, version.assets]))}`;
    return (
      <LivePagePoster
        key={`${cacheKey}:${hero}:${immersive ? 'screen' : 'page'}`}
        cacheKey={cacheKey}
        imageUrl={hero || undefined}
        title={t('{{name}} preview', { name: p.name || t('Untitled landing page') })}
        layout={immersive ? 'screen' : 'mobile'}
        loadDoc={() =>
          previewLandingPage(tenantId, version.params, undefined, version.assets).then((res) =>
            res.ok && res.data.html ? res.data.html : null,
          )
        }
      />
    );
  };

  const archivedCount = listed.filter((p) => p.archived).length;
  const statsForPage = statsForId ? listed.find((p) => p.id === statsForId && !p.archived) : undefined;
  const linksForPage = linksForId ? listed.find((p) => p.id === linksForId && !p.archived) : undefined;
  // Every link landing on the page being archived while live (its own included): what taking it down cuts off.
  const archiveLinks = archiveLive ? allLinkCounts.get(archiveLive.page.id) ?? 0 : 0;

  // The template step of a page made for an asset says which asset (a deep link back to it).
  const forAssetBar = linkForQuery ? (
    <MadeForBar
      assets={[
        {
          key: `${linkForQuery.creativeId}:${linkForQuery.versionKey}`,
          name: linkForQuery.name,
          path: assetLinkPath(linkForQuery.kind, linkForQuery.creativeId, linkForQuery),
        },
      ]}
      state="draft"
      onOpen={(path) => navigate(path)}
    />
  ) : null;

  const viewsNote = onGrid ? viewsNoteOf(views) : null;

  return (
    <>
      <S.DraftsBlock>
        {viewsNote ? <Callout tone="info" text={viewsNote.text} placeholders={viewsNote.placeholders} /> : null}
        <AdLibraryShell<LibraryEntry>
          kind="landing_page"
          items={entries}
          // The final list is the brand's own pages AND the pages live on its landing domain (read on their own).
          ready={settled && (tenantId === undefined || current !== null)}
          title={t('Your landing pages')}
          intro={t(
            'The pages your ads and links send players to. Pick a full-screen game or an offer page, add your offer, let Lumio draw its art and write its copy, then publish it on your landing domain.',
          )}
          archivedTitle={t('Archived landing pages ({{n}})', { n: archivedCount })}
          archivedEmptyText={t('No archived landing pages.')}
          emptyText={
            listFailure === 404
              ? t("Saving landing pages isn't available in this environment yet.")
              : listFailure !== null
                ? t("Your landing pages couldn't be loaded. Reload the page to try again.")
                : t('No landing pages yet. Create your first one.')
          }
          loadingText={t('Loading your landing pages…')}
          createLabel={t('Create new')}
          createAriaLabel={t('Create a new landing page')}
          untitledName={t('Untitled landing page')}
          archiveConfirmTitle={t('Archive this landing page?')}
          archiveConfirmBody={t(
            'It will be moved to Archived landing pages, where you can restore it later or permanently delete it. A live page stays live until you unpublish it.',
          )}
          posterOf={(e) =>
            isLiveEntry(e) ? (
              <LivePagePoster html={e.live.html} title={t('{{name}} preview', { name: e.name })} layout="mobile" />
            ) : (
              ownPoster(e)
            )
          }
          thumbOf={(p) => {
            if (isLiveEntry(p)) return undefined;
            const version = landingPosterVersion(p);
            return landingHeroUrl(version) || version?.params.scene.background_mobile_url || p.image_url || undefined;
          }}
          metaOf={(p) => {
            if (isLiveEntry(p)) {
              return t('{{offer}} · {{path}}', { offer: offerLabel(p.live.moid), path: p.live.path });
            }
            const offer = p.ad_params.offer.moid ? offerLabel(p.ad_params.offer.moid) : t('No offer');
            const langs = p.ad_params.languages.map((l) => l.toUpperCase()).join(', ');
            return langs ? t('{{offer}} · {{languages}}', { offer, languages: langs }) : offer;
          }}
          stateOf={(p) => (isLiveEntry(p) ? 'ready' : cardState(p, jobsById))}
          // A page on the site reads Live, whether it was found live or published from the Studio.
          statusOf={(p) =>
            isLiveEntry(p) || p.publication?.state === 'live' ? { label: t('Live'), tone: 'ready' } : undefined
          }
          readOnlyOf={isLiveEntry}
          cardFooterSkeleton={<AssetCardStatsSkeleton withViews={traffic.websiteAnalytics} />}
          cardFooterOf={(p) =>
            isLiveEntry(p) ? (
              liveLine(p.live)
            ) : (
              <>
                {publicationLine(p)}
                <AssetCardStats
                  loaded={stats.loaded}
                  failed={stats.failed}
                  stats={stats.byId[p.id]}
                  hint="landing-page"
                  withViews={traffic.websiteAnalytics}
                  views={viewsOf(p)}
                  viewsLoading={views.status === 'loading' && Boolean(p.publication?.path)}
                  onOpen={() => statsSheet.open(p.id)}
                />
              </>
            )
          }
          onCreate={startNew}
          onOpen={(p) => (isLiveEntry(p) ? setViewing(p.live) : openPage(p))}
          remove={removePage}
          restore={restore}
          purge={purgePage}
          jobsById={jobsById}
        />
      </S.DraftsBlock>

      {liveNotice()}

      {statsForPage ? (
        <AssetStatsSheet
          kind="landing_page"
          creativeId={statsForPage.id}
          name={statsForPage.name}
          landingPath={statsForPage.publication?.path}
          onClose={() => statsSheet.close()}
        />
      ) : null}

      {linksForPage && heldLinks ? (
        <LandingLinks
          pageId={linksForPage.id}
          read={{
            status: 'ready',
            links: heldLinks.filter(
              (link) => link.destination_type === 'landing_page' && link.landing_page_id === linksForPage.id,
            ),
            reading: false,
            reload: stats.refresh,
          }}
          // Leaving keeps the sheet's entry: Back comes back to it open.
          onOpen={(path) => navigate(path)}
          onClose={() => linksSheet.close()}
        />
      ) : null}

      {viewing ? (
        <Sheet
          title={viewing.title || viewing.name || viewing.path}
          onClose={() => setViewing(null)}
          closeHandle={viewingCloseHandle}
          maxWidth="1100px"
        >
          <S.Viewer>
            <Callout
              tone="info"
              text={t(
                'This page was made outside the Studio. You can view it and copy its address here, but it can only be edited where it was made.',
              )}
            />
            <S.ViewerFacts>
              {viewing.url ? (
                <S.UrlRow>
                  <S.UrlText raw text={viewing.url} title={viewing.url} />
                  <CopyButton
                    value={viewing.url}
                    label={t('Copy address')}
                    onCopied={() => onNotify(t('Address copied'))}
                  />
                  <S.OpenLink
                    href={viewing.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={t('Open {{name}} in a new tab', { name: viewing.title || viewing.path })}
                    title={t('Open in a new tab')}
                  >
                    <Icon name="external-link" size={14} />
                  </S.OpenLink>
                </S.UrlRow>
              ) : (
                <S.Meta text="No public address yet" />
              )}
              <S.OfferRow>
                <Icon name="megaphone" size={12} />
                <S.Meta raw text={t('{{offer}} · {{path}}', { offer: offerLabel(viewing.moid), path: viewing.path })} />
              </S.OfferRow>
            </S.ViewerFacts>
            <S.ViewerPreview>
              <LandingPreview
                html={viewing.html ? liveFragmentDocument(viewing.html, viewing.title || viewing.path) : null}
                busy={false}
                failure={null}
                waitingText={viewing.html ? null : t('This page has nothing to preview.')}
              />
            </S.ViewerPreview>
          </S.Viewer>
        </Sheet>
      ) : null}

      {composerNode}

      {archiveLive ? (
        <Sheet
          title={t('Unpublish and archive?')}
          onClose={() => {
            if (!takingDown) cancelArchiveLive();
          }}
          closeHandle={archiveLiveCloseHandle}
          maxWidth="460px"
          footer={
            <S.ConfirmFoot>
              <Button variant="text" label={t('Cancel')} onClick={dismissArchiveLive} disabled={takingDown} />
              <Button
                variant="primary"
                label={takingDown ? t('Unpublishing…') : t('Unpublish and archive')}
                disabled={takingDown}
                onClick={() => void confirmArchiveLive()}
              />
            </S.ConfirmFoot>
          }
        >
          <S.ConfirmStack>
            <S.ConfirmBody
              text="{{url}} is live. It is taken offline first (visitors to it are sent to your casino's home page), then archived. This can take a few seconds."
              placeholders={{ url: archiveLive.page.publication?.url || archiveLive.page.name }}
            />
            {archiveLinks > 0 ? (
              <Callout
                tone="warning"
                text={
                  archiveLinks === 1
                    ? t(
                        "1 tracking link sends visitors here. Unpublishing sends them to your casino's home page and its tracking is lost.",
                      )
                    : t(
                        "{{n}} tracking links send visitors here. Unpublishing sends them to your casino's home page and their tracking is lost.",
                        { n: archiveLinks },
                      )
                }
              />
            ) : null}
          </S.ConfirmStack>
        </Sheet>
      ) : null}

      {/* A NEW page: first what it promotes (a promotion or up to 4 games), then its draft is saved and the composer
          opens on it. */}
      {autoPending && !minting ? (
        <Sheet onClose={goToLibrary} ariaLabel={t('New landing page')} maxWidth="1100px" maxHeight="100%">
          <S.NewForAsset>
            {forAssetBar}
            <Skeleton block height={320} radius={12} />
          </S.NewForAsset>
        </Sheet>
      ) : null}
      {view.kind === 'new' && !created && !minting && !choice && !autoPending ? (
        <AdSubjectFlow
          items={items}
          itemsLoaded={itemsLoaded}
          subjectTypes={['promotion', 'game']}
          gamesMax={LANDING_FEATURED_GAMES_MAX}
          promotionTitle={t('Choose the promotion this landing page promotes')}
          promotionHint={t('Pick one. The page leads with it, and its button always claims your welcome offer.')}
          busy={minting}
          onChosen={chosePromotion}
          onChosenGames={choseGames}
          onClose={goToLibrary}
        />
      ) : null}
      {view.kind === 'new' && !created && !minting && choice && tenantId ? (
        <Sheet onClose={goToLibrary} ariaLabel={t('New landing page')} maxWidth="1100px" maxHeight="100%">
          {/* The template step waits for the promotion pool too: the page is saved with its welcome offer. */}
          <S.NewForAsset>
            {forAssetBar}
            {choiceParams && brandFacts && itemsLoaded ? (
              <LandingTemplatePicker
                tenantId={tenantId}
                templates={templatesRead}
                params={choiceParams}
                selectedId=""
                canCancel
                onPick={(id) => mint(choice, brandFacts, id)}
                onCancel={() => setChoice(null)}
              />
            ) : (
              <Skeleton block height={320} radius={12} />
            )}
          </S.NewForAsset>
        </Sheet>
      ) : null}
      {view.kind === 'new' && !created && minting ? (
        <Sheet onClose={goToLibrary} ariaLabel={t('New landing page')} maxWidth="460px">
          <S.Minting>
            <Skeleton block height={120} radius={12} />
            <S.MintingText text="Setting up your landing page…" />
          </S.Minting>
        </Sheet>
      ) : null}
    </>
  );
};

export default LandingPageLibrary;
